import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage, FormattedNumber, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled from 'styled-components';

import { ShippingMethods } from '@/enums/shippingMethod';
import { shippingProviders } from '@/enums/shippingProviders';
import { useAppSelector } from '@/redux/hooks';
import { getShippingQuoteError } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { ShippingQuote } from '@/types/ShippingQuote';

import ShippingMethodLabel from '../ShippingMethodLabel';

export type LiveShippingShippingMethodProps = {
    loading?: boolean;
    numberOfItems: number;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    selectedName?: string;
    shippingQuote: ShippingQuote;
};

const LiveShippingShippingMethod = ({
    loading,
    numberOfItems,
    onClick,
    selectedName,
    shippingQuote,
}: LiveShippingShippingMethodProps) => {
    const { formatMessage } = useIntl();
    const quoteErrorCode = useAppSelector((state) => getShippingQuoteError(state));

    const { currency = 'USD', shippingProvider, total = 0, transitDays } = shippingQuote;

    return (
        <StyledRadio
            disabled={Boolean(quoteErrorCode) || loading}
            label={
                <ShippingMethodLabel
                    info={
                        quoteErrorCode ? (
                            <StyledErrorText
                                colorError
                                secondary
                            >
                                <FormattedMessage
                                    id={
                                        quoteErrorCode === 500
                                            ? 'checkout.shippingMethod.quoteError500'
                                            : 'checkout.shippingMethod.quoteError400'
                                    }
                                />
                            </StyledErrorText>
                        ) : (
                            <span className="block">
                                {React.Children.toArray(
                                    formatMessage(
                                        {
                                            id: transitDays
                                                ? 'checkout.shippingMethod.method.laShippingDetails'
                                                : 'checkout.shippingMethod.method.laShippingDetailsNoTransitDays',
                                        },
                                        {
                                            days: transitDays || 1,
                                            numberOfItems,
                                            shippingProvider: shippingProviders[shippingProvider],
                                        }
                                    )
                                )}
                            </span>
                        )
                    }
                    popoverContent={<FormattedMessage id="checkout.shippingMethod.popovers.laShip" />}
                    price={
                        !quoteErrorCode && (
                            <FormattedNumber
                                currency={currency}
                                key="checkout-shipping-method-live-shipping-price"
                                maximumFractionDigits={2}
                                minimumFractionDigits={2}
                                style="currency"
                                value={Number(total)}
                            />
                        )
                    }
                    title={<FormattedMessage id="checkout.shippingMethod.method.A" />}
                />
            }
            name={ShippingMethods.LiveShipping}
            onClick={onClick}
            selectedName={selectedName}
            testid="shippingMethodLiveShipping"
        />
    );
};

export default LiveShippingShippingMethod;

const StyledRadio = styled(Radio).attrs(() => ({
    radioCircleClassName: 'radioCircleClassName',
}))`
    padding-left: 26px;
    .radioCircleClassName {
        top: 2px;
        left: 0;
    }

    & > span:first-of-type {
        width: 100%;
    }
`;

const StyledErrorText = styled(Text)`
    margin-top: 2px;
`;
